import React from 'react';
import { ReportSection, ReportSubsection } from '../framework';
import { CalloutBox, StatCard, DataTable } from '../primitives';
import { SectionProps } from './types';
import { Citation } from '../Citation';

export const PropertySection: React.FC<SectionProps> = ({ assumptions, formatCurrency, citations }) => {
  // Calculate fixed annual expenses (same formula as main calculations)
  const calcFixedExpenses = (propertyValue: number) => {
    const propertyTax = propertyValue * assumptions.propertyTaxRate / 100;
    const insurance = assumptions.insuranceCost;
    const maintenance = propertyValue * assumptions.maintenanceRate / 100;
    return propertyTax + insurance + maintenance;
  };

  // Calculate net income for a given monthly rent
  const calcNetIncome = (monthlyRent: number) => {
    const annualRent = monthlyRent * 12;
    const vacancyLoss = annualRent * assumptions.vacancyRate / 100;
    const grossEffective = annualRent - vacancyLoss;
    const managementCost = grossEffective * assumptions.managementFee / 100;
    const fixedExpenses = calcFixedExpenses(assumptions.propertyValue);
    return Math.round(grossEffective - managementCost - fixedExpenses);
  };

  // Generate rent scenario rows
  const baseRent = assumptions.monthlyRent;
  const lowRent = Math.round(baseRent * 0.93); // ~7% below baseline
  const highRent = Math.round(baseRent * 1.07); // ~7% above baseline
  const premiumRent = Math.round(baseRent * 1.14); // ~14% above baseline

  const rentScenarios = [
    {
      rent: formatCurrency(lowRent),
      annual: formatCurrency(lowRent * 12),
      net: `~${formatCurrency(calcNetIncome(lowRent))}`,
      yield: `${(lowRent * 12 / assumptions.propertyValue * 100).toFixed(1)}%`,
      scenario: 'Conservative—older home, minimal updates'
    },
    {
      rent: formatCurrency(baseRent),
      annual: formatCurrency(baseRent * 12),
      net: `~${formatCurrency(calcNetIncome(baseRent))}`,
      yield: `${(baseRent * 12 / assumptions.propertyValue * 100).toFixed(1)}%`,
      scenario: 'Zillow estimate (baseline)',
      highlighted: true
    },
    {
      rent: formatCurrency(highRent),
      annual: formatCurrency(highRent * 12),
      net: `~${formatCurrency(calcNetIncome(highRent))}`,
      yield: `${(highRent * 12 / assumptions.propertyValue * 100).toFixed(1)}%`,
      scenario: 'Well-presented, good condition'
    },
    {
      rent: formatCurrency(premiumRent),
      annual: formatCurrency(premiumRent * 12),
      net: `~${formatCurrency(calcNetIncome(premiumRent))}`,
      yield: `${(premiumRent * 12 / assumptions.propertyValue * 100).toFixed(1)}%`,
      scenario: 'Premium—updated, well-marketed, ideal tenant'
    },
  ];

  const growthScenarios = [
    { growth: '0% (flat)', year1: formatCurrency(assumptions.monthlyRent), year10: formatCurrency(assumptions.monthlyRent), scenario: 'Rents already at ceiling, no real growth' },
    { growth: '1% (baseline)', year1: formatCurrency(assumptions.monthlyRent), year10: formatCurrency(Math.round(assumptions.monthlyRent * Math.pow(1.01, 10))), scenario: 'Modest growth, used in balanced scenario', highlighted: true },
    { growth: '2%', year1: formatCurrency(assumptions.monthlyRent), year10: formatCurrency(Math.round(assumptions.monthlyRent * Math.pow(1.02, 10))), scenario: 'Strong growth, supply-constrained market' },
    { growth: '3%', year1: formatCurrency(assumptions.monthlyRent), year10: formatCurrency(Math.round(assumptions.monthlyRent * Math.pow(1.03, 10))), scenario: 'Very strong—continued wealth migration' },
  ];

  return (
    <ReportSection id="property" title="The Property" subtitle="2040 Beryl Lane: The specifics">
      <p className="text-gray-700 mb-6">
        Now let's look at the actual property we're analyzing. This is a single-family home in Newport Beach—
        not a luxury estate, but a solid property in one of America's most desirable coastal communities.
      </p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <StatCard label="Bedrooms" value="3" />
        <StatCard label="Bathrooms" value="2" />
        <StatCard label="Square Feet" value="1,489" />
        <StatCard label="Year Built" value="1954" />
      </div>

      <ReportSubsection title="Current Valuation">
        <p className="text-gray-700 mb-4">
          Two leading real estate platforms provide automated valuations:
        </p>

        <div className="grid md:grid-cols-2 gap-4 mb-6">
          <StatCard label="Zillow Zestimate" value={formatCurrency(assumptions.propertyValue)} sublabel="December 2025" />
          <StatCard label="Redfin Estimate" value={formatCurrency(Math.round(assumptions.propertyValue * 1.055))} sublabel="December 2025" />
        </div>

        <p className="text-gray-600 text-sm mb-4">
          The estimates are within 5% of each other—reasonable consensus. We'll use the{' '}
          <Citation citation={citations.zillow}>Zillow estimate of {formatCurrency(assumptions.propertyValue)}</Citation> as
          the baseline, though you can adjust this in the simulator.
        </p>

        <p className="text-gray-700">
          For context: at {formatCurrency(assumptions.propertyValue)}, this is among the more affordable single-family homes in Newport Beach,
          where newer and larger properties often list for $3-5M+. This positioning may be relevant for rental demand—there's
          a market for people who want to live in Newport Beach but are priced out of the higher end.
        </p>
      </ReportSubsection>

      <ReportSubsection title="Rental Potential">
        <p className="text-gray-700 mb-4">
          <Citation citation={citations.zillow}>Zillow's Rent Zestimate</Citation> for this property
          is <strong>{formatCurrency(assumptions.monthlyRent)}/month</strong> ({formatCurrency(assumptions.monthlyRent * 12)}/year).
          But rent estimates have significant uncertainty—the actual achievable rent depends on the property's
          condition, updates, how it's marketed, and market timing.
        </p>

        <p className="text-gray-700 mb-4">
          A reasonable range for this property might be <strong>{formatCurrency(lowRent)}–{formatCurrency(premiumRent)}/month</strong>:
        </p>

        <DataTable
          columns={[
            { key: 'rent', header: 'Monthly Rent', align: 'left' },
            { key: 'annual', header: 'Annual Gross', align: 'right' },
            { key: 'net', header: 'Est. Net Income*', align: 'right' },
            { key: 'yield', header: 'Gross Yield', align: 'right' },
            { key: 'scenario', header: 'Scenario', align: 'left', muted: true }
          ]}
          data={rentScenarios}
          highlightRow={(row) => row.highlighted === true}
          bordered
          caption={`*Net income = gross rent minus all expenses (property tax, insurance, maintenance, vacancy, management). Before income taxes. See expense breakdown in "Option A: Keep & Rent" section.`}
        />

        <CalloutBox type="insight" title="Why This Matters">
          <p className="mb-2">
            The difference between {formatCurrency(lowRent)} and {formatCurrency(premiumRent)}/month is <strong>{formatCurrency((premiumRent - lowRent) * 12)}/year in gross rent</strong>—but
            because expenses are mostly fixed, it's roughly <strong>{formatCurrency(calcNetIncome(premiumRent) - calcNetIncome(lowRent))}/year difference in net income</strong>.
            Over 10 years with compounding, that's a significant swing in the rental scenario outcome.
          </p>
          <p>
            If you're confident you can achieve {formatCurrency(premiumRent)}/month (through updates, good marketing, or tenant selection),
            the rental option becomes significantly more attractive. If you're less certain and expect closer to {formatCurrency(lowRent)},
            the math tilts further toward selling.
          </p>
        </CalloutBox>

        <p className="text-gray-700 mt-4 mb-4">
          For the analysis below, we use the Zillow estimate of {formatCurrency(assumptions.monthlyRent)}/month. But keep in mind:
          this is a central estimate, not a guarantee. The property's actual rental performance could meaningfully
          outperform or underperform this baseline.
        </p>

        <p className="text-gray-600 text-sm">
          Newport Beach commands premium rents due to its coastal location, excellent schools, and limited housing supply.
          The <Citation citation={citations.apartmentList}>median rent in Newport Beach</Citation> is significantly higher than
          surrounding areas, which supports the higher end of this range for a well-positioned property.
        </p>
      </ReportSubsection>

      <ReportSubsection title="Why Newport Beach is Unusual">
        <p className="text-gray-700 mb-4">
          This isn't just any rental property. Newport Beach has characteristics that make it different from
          most real estate markets:
        </p>

        <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
          <li><strong>Hard supply constraint:</strong> The city is largely built out. Ocean on one side, established
          cities on the other. New construction is minimal.</li>
          <li><strong>High-income renter base:</strong> Tech workers, executives, and professionals who want coastal
          living but aren't ready to buy at $3M+.</li>
          <li><strong>School district premium:</strong> Top-rated schools drive consistent demand from families.</li>
          <li><strong>Trophy market dynamics:</strong> At some point, prices are driven by wealthy buyers who want
          to own in Newport Beach regardless of "fair value."</li>
        </ul>

        <p className="text-gray-700">
          These factors explain the historical outperformance, but also introduce risk. If you believe these
          structural advantages will persist, the rental option looks better. If you think Newport Beach is
          a bubble waiting to pop, selling looks better.
        </p>
      </ReportSubsection>

      <ReportSubsection title="Rent Growth: What Can You Expect?">
        <p className="text-gray-700 mb-4">
          The model uses <strong>{assumptions.rentGrowth}% real rent growth</strong> per year. But rent growth is
          highly uncertain—here's what different scenarios look like over 10 years:
        </p>

        <DataTable
          columns={[
            { key: 'growth', header: 'Rent Growth (Real)', align: 'left' },
            { key: 'year1', header: 'Starting Rent', align: 'right' },
            { key: 'year10', header: 'After 10 Years', align: 'right' },
            { key: 'scenario', header: 'Scenario', align: 'left', muted: true }
          ]}
          data={growthScenarios}
          highlightRow={(row) => row.highlighted === true}
          bordered
        />

        <p className="text-gray-700 mt-4 mb-4">
          <strong>The bull case for rent growth:</strong> Newport Beach's supply constraints are real and permanent.
          As more wealth concentrates in coastal California, competition for limited housing pushes rents up.
          Remote work has made location even more valuable for those who can afford it.
        </p>

        <p className="text-gray-700 mb-4">
          <strong>The bear case:</strong> Newport Beach rents are already at the high end of what the market will bear.
          A {formatCurrency(baseRent)}/month rent requires household income of ~{formatCurrency(Math.round(baseRent * 12 / 0.30 / 1000) * 1000)} (using the 30% rule). The pool of renters at that level,
          who also <em>want</em> to rent rather than buy, is limited. At some point, you hit a ceiling.
        </p>

        <p className="text-gray-600 text-sm">
          The 1% baseline is conservative—if you believe Newport Beach's structural advantages drive faster rent growth,
          use the scenario toggle in the comparison section to see how higher growth changes the outcome.
        </p>
      </ReportSubsection>
    </ReportSection>
  );
};
